import { roundDecimal, sumMoney } from '@mms/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import { dateReq, selectReq, toId, toText } from '../forms/zod';
import { useBalances } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { useToast } from '../ui/feedback';
import { nowInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import { FieldRenderer, invalidateData, type CrudConfig, type FieldDef } from './CrudScreen';

/**
 * Bank master (redesigned 2026-10-16, migration 033): totals by currency along the top, a masked account number, Status, the balance in the currency's own
 * decimals (an overdrawn account in red), safe Deactivate / Delete, and a form in two sections with optional IBAN and SWIFT, a Balance as of date and an Active
 * toggle. With transactions the Currency, Opening balance and Balance as of are locked and the bank can only be deactivated. Opening balances are stored with
 * 2 decimals whatever the currency's decimal places are.
 */
const STORED_DP = 2;
const strip = (v: string) => v.replace(/,/g, '').trim();
const alnum = (v: string) => v.replace(/[^A-Za-z0-9]/g, '');
/** "1012345678" -> "•••• 5678" */
const mask = (account: string) => `•••• ${alnum(account).slice(-4)}`;
/** A signed decimal string rounded half-up to \`dp\` decimals; anything that is not a number is returned as typed. */
const fmtSigned = (v: string, dp: number): string => {
  const s = strip(v);
  const neg = s.startsWith('-');
  const r = roundDecimal(s.replace(/^-/, ''), dp);
  return r === null ? s : `${neg ? '-' : ''}${r}`;
};
const group = (v: string) => v.replace(/^(-?)(\d+)/, (_m, sign: string, int: string) => sign + int.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
const LOCKED = 'Locked: this bank has transactions';

const bankSchema = z.object({
  name: z.string().trim().min(1, 'Required').regex(/^[A-Za-z0-9 &.'-]+$/, "Use letters, numbers, spaces and & . ' - only"),
  location: z.string().trim().min(1, 'Required').regex(/^[A-Za-z ,.'-]+$/, 'Use letters, spaces, commas, hyphens and periods only'),
  accountNo: z.string().trim().min(1, 'Required')
    .refine((v) => /^[A-Za-z0-9 -]+$/.test(v) && alnum(v).length >= 5, 'Enter at least 5 letters or numbers (spaces and hyphens allowed)'),
  iban: z.string().refine((v) => v === '' || /^[A-Z0-9]{15,34}$/.test(v), 'An IBAN has 15 to 34 letters and numbers'),
  swift: z.string().refine((v) => v === '' || /^([A-Z0-9]{8}|[A-Z0-9]{11})$/.test(v), 'A SWIFT code has 8 or 11 characters'),
  currencyId: selectReq('Select the currency'),
  openingBalance: z.string().trim().min(1, 'Enter 0 if none').regex(/^-?\d+(\.\d{1,4})?$/, 'Use a number'),
  balanceAsOf: dateReq,
  isActive: z.boolean(),
});

/** Deactivate / Activate: a safe alternative to Delete that keeps the history. */
function useBankActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = useCan('banks', 'edit');
  const toggle = useMutation({
    mutationFn: (row: Row) => api.patch(`/banks/${row.id}`, { isActive: !row.isActive }),
    onSuccess: async (_d, row) => {
      await invalidateData(qc);
      toast.success(row.isActive ? `${row.name} (${row.currencyCode}) deactivated. It is hidden from dropdowns, history is kept.` : `${row.name} (${row.currencyCode}) activated`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not change the status'),
  });
  return (row: Row) => canEdit ? (
    <Button size="sm" variant="outline-secondary" className="me-1" disabled={toggle.isPending} onClick={() => toggle.mutate(row)}
      title={row.isActive ? 'Deactivate' : 'Activate'} aria-label={`${row.isActive ? 'Deactivate' : 'Activate'} ${row.name} (${row.currencyCode})`}>
      <i className={`bi ${row.isActive ? 'bi-pause-fill' : 'bi-play-fill'}`} aria-hidden />
    </Button>
  ) : null;
}

/** Totals of the ACTIVE banks, one chip per currency, in the currency's own decimals. */
function Totals() {
  const balances = useBalances('banks');
  const banks = useLookup('banks'); // active banks only
  const currencies = useLookup('currencies', { all: 1 });
  const active = new Set((banks.data ?? []).map((b) => b.id));
  const dp = new Map((currencies.data ?? []).map((c) => [c.code as string, Number(c.decimals ?? 2)]));
  const sums = new Map<string, string[]>();
  for (const b of balances.data ?? []) {
    if (!active.has(b.id)) continue;
    sums.set(b.currencyCode, [...(sums.get(b.currencyCode) ?? []), String(b.balance)]);
  }
  const chips = [...sums.entries()].sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="d-flex flex-wrap gap-2 mb-3" data-testid="bank-totals">
      {chips.map(([code, values]) => {
        const total = sumMoney(values, 3);
        return (
          <div key={code} className="rounded bg-body-tertiary px-3 py-2">
            <div className="small text-body-secondary">Total {code}</div>
            <strong className={Number(total) < 0 ? 'text-danger' : ''}>{group(fmtSigned(total, dp.get(code) ?? 2))}</strong>
          </div>
        );
      })}
      {!balances.isPending && !banks.isPending && chips.length === 0 && <span className="small text-body-secondary align-self-center">No active banks.</span>}
    </div>
  );
}

export const banksConfig: CrudConfig = {
  title: 'Banks', singular: 'bank', icon: 'bank', menu: 'banks', endpoint: '/banks', update: 'patch', modalSize: 'lg',
  confirmDiscard: true,
  searchPlaceholder: 'Search bank, location or account',
  schema: bankSchema,
  defaults: () => ({ name: '', location: '', accountNo: '', iban: '', swift: '', currencyId: '', openingBalance: '', balanceAsOf: nowInput().slice(0, 10), isActive: true }),
  toForm: (r) => ({
    name: r.name, location: r.location ?? '', accountNo: r.accountNo, iban: r.iban ?? '', swift: r.swift ?? '', currencyId: String(r.currencyId),
    openingBalance: String(r.openingBalance), balanceAsOf: String(r.balanceAsOf).slice(0, 10), isActive: r.isActive !== false,
  }),
  toPayload: (v) => ({
    name: v.name.trim(), location: toText(v.location), accountNo: v.accountNo.trim(), iban: toText(v.iban), swift: toText(v.swift), currencyId: toId(v.currencyId),
    openingBalance: fmtSigned(v.openingBalance, STORED_DP), balanceAsOf: v.balanceAsOf, isActive: !!v.isActive,
  }),
  rowLabel: (r) => `${r.name} (${r.currencyCode})`,
  initialSort: { key: 'name', dir: 'asc' },
  useRowActions: useBankActions,
  useAbove: () => <Totals />,
  useDeleteBlocked: () => {
    const balances = useBalances('banks');
    const byId = new Map((balances.data ?? []).map((b) => [b.id, b]));
    return (r) => (r.hasTransactions || Number(byId.get(r.id)?.balance ?? 0) !== 0 ? 'Has transactions or a balance. Deactivate instead' : null);
  },
  useTable: () => {
    const balances = useBalances('banks');
    const currencies = useLookup('currencies', { all: 1 });
    const byId = new Map((balances.data ?? []).map((b) => [b.id, b]));
    const dp = new Map((currencies.data ?? []).map((c) => [c.code as string, Number(c.decimals ?? 2)]));
    const dim = (r: Row) => (r.isActive === false ? 'text-body-secondary' : '');
    const columns: Column[] = [
      { key: 'name', header: 'Bank', sortKey: 'name', className: 'fw-semibold', render: (r) => <span className={dim(r)}>{r.name}</span> },
      { key: 'accountNo', header: 'Account no', hideOnMobile: true, render: (r) => <span className={dim(r)}>{mask(String(r.accountNo))}</span> },
      { key: 'location', header: 'Location', sortKey: 'location', hideOnMobile: true, render: (r) => <span className={dim(r)}>{r.location ?? ''}</span> },
      { key: 'currencyCode', header: 'Currency', render: (r) => <span className={`fw-semibold ${dim(r)}`}>{r.currencyCode}</span> },
      { key: 'isActive', header: 'Status', render: (r) => <span className={`badge text-bg-${r.isActive === false ? 'secondary' : 'success'}`}>{r.isActive === false ? 'Inactive' : 'Active'}</span> },
      {
        key: 'balance', header: 'Current balance', align: 'end', className: 'fw-semibold',
        render: (r) => {
          if (balances.isPending) return <span className="placeholder-glow"><span className="placeholder col-12" style={{ width: 70 }} /></span>;
          const v = String(byId.get(r.id)?.balance ?? '0');
          const neg = Number(v) < 0;
          return (
            <span title={neg ? 'Overdrawn' : undefined}>
              <span className={neg ? 'text-danger' : dim(r)}>{group(fmtSigned(v, dp.get(String(r.currencyCode)) ?? 2))}</span>
              <small className="text-body-secondary ms-1">{r.currencyCode}</small>
            </span>
          );
        },
      },
    ];
    return {
      columns,
      filters: [
        { param: 'currencyId', label: 'Currency', options: toOptions(currencies.data?.filter((c) => c.isActive !== false), (c) => c.code as string) },
        { param: 'isActive', label: 'Status', allLabel: 'All statuses', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
      ],
    };
  },
  useForm: (form, { mode, row, readOnly }) => {
    const currencies = useLookup('currencies', { all: 1 });
    const name = String(form.watch('name') ?? '').trim();
    const account = String(form.watch('accountNo') ?? '');
    const iban = String(form.watch('iban') ?? '');
    const swift = String(form.watch('swift') ?? '');
    const currencyId = String(form.watch('currencyId') ?? '');
    const locked = mode === 'edit' && !!row?.hasTransactions;
    const chosen = (currencies.data ?? []).find((c) => String(c.id) === currencyId);
    const dp = Number(chosen?.decimals ?? 2);

    // IBAN and SWIFT: upper case, letters and numbers only
    useEffect(() => { const v = alnum(iban).toUpperCase(); if (v !== iban) form.setValue('iban', v, { shouldDirty: true }); }, [iban]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { const v = alnum(swift).toUpperCase().slice(0, 11); if (v !== swift) form.setValue('swift', v, { shouldDirty: true }); }, [swift]); // eslint-disable-line react-hooks/exhaustive-deps
    // changing the currency reformats the opening balance to its decimal places straight away
    const lastCurrency = useRef(currencyId);
    useEffect(() => {
      if (lastCurrency.current === currencyId) return;
      lastCurrency.current = currencyId;
      const raw = String(form.getValues('openingBalance') ?? '');
      if (roundDecimal(strip(raw).replace(/^-/, ''), 0) !== null) form.setValue('openingBalance', fmtSigned(raw, dp), { shouldDirty: true });
    }, [currencyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeCurrencies = (currencies.data ?? []).filter((c) => c.isActive !== false || String(c.id) === currencyId);
    const field = (def: FieldDef, col: number) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const section = (title: string, children: React.ReactNode) => (
      <div className="card mb-3"><div className="card-header fw-semibold py-2">{title}</div><div className="card-body pb-0"><div className="row">{children}</div></div></div>
    );
    const body = (
      <>
        {section('Bank details', <>
          {field({ name: 'name', label: 'Bank name', type: 'text', required: true, help: 'For example Emirates NBD' }, 6)}
          {field({ name: 'location', label: 'Branch / location', type: 'text', required: true, help: 'For example Dubai, Deira branch' }, 6)}
          {field({ name: 'accountNo', label: 'Account number', type: 'text', required: true, help: 'Letters, numbers, spaces or hyphens' }, 4)}
          {field({ name: 'iban', label: 'IBAN (optional)', type: 'text' }, 4)}
          {field({ name: 'swift', label: 'SWIFT / BIC (optional)', type: 'text', maxLength: 11 }, 4)}
        </>)}
        {section('Account and balance', <>
          {field({
            name: 'currencyId', label: 'Currency', type: 'select', required: true, placeholder: 'Select…', disabled: locked,
            options: toOptions(activeCurrencies, (c) => c.code as string), help: locked ? LOCKED : 'Only active currencies are listed',
          }, 4)}
          {field({ name: 'openingBalance', label: 'Opening balance', type: 'money', required: true, disabled: locked, help: locked ? LOCKED : 'Enter 0 if none' }, 4)}
          {field({ name: 'balanceAsOf', label: 'Balance as of', type: 'date', required: true, disabled: locked, help: locked ? LOCKED : undefined }, 4)}
          {field({ name: 'isActive', label: 'Active', type: 'checkbox', help: 'Inactive banks are hidden from Payment, Receipt and Fund Transfer dropdowns' }, 12)}
        </>)}
        <div className="rounded bg-body-tertiary px-3 py-2" data-testid="bank-preview">
          <div className="small text-body-secondary">Shown in dropdowns as</div>
          <strong>{name || 'Bank name'} – {chosen?.code ?? 'CUR'}{alnum(account) ? ` (…${alnum(account).slice(-4)})` : ''}</strong>
        </div>
      </>
    );
    return { body, fields: [] };
  },
};
